// Settings.jsx — Admin (Users / Application / Security)
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./Settings.css";
import AdminUsersTab from "./users/AdminUsersTab";

const settingsCache = new Map();
const inflight = new Map();

/* ---------- Security dashboard metrics ---------- */
function Metric({ label, value, hint }) {
  return (
    <div className="metric-card">
      <div className="metric-value">{value ?? "—"}</div>
      <div className="metric-label">{label}</div>
      {hint ? <div className="metric-hint">{hint}</div> : null}
    </div>
  );
}

function SecurityOverview() {
  const [stats, setStats] = React.useState(null);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setErr("");
        const res = await axios.get("/api/admin/access_stats.php", { withCredentials: true });
        if (alive) setStats(res.data);
      } catch {
        if (alive) setErr("Failed to load access statistics");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const g = stats?.globals || {};
  const m = stats?.mfa || {};
  const loc = stats?.locations || [];
  const stale = stats?.stale_users || [];

  return (
    <div className="settings-card">
      <div className="metrics-grid">
        {/* Global counts */}
        <Metric label="Total users" value={g.total_users} />
        <Metric label="Active users" value={g.active_users} />
        <Metric label="Inactive users" value={g.inactive_users} />

        {/* Roles */}
        <Metric label="Admins" value={g.role_admin} />
        <Metric label="Users" value={g.role_user} />
        <Metric label="Viewers" value={g.role_viewer} />

        {/* Activity */}
        <Metric label="Logged last 30 days" value={g.logged_30d} />
        <Metric label="Stale ≥ 90 days" value={g.stale_90d} />
        <Metric label="Never logged" value={g.never_logged} />

        {/* MFA */}
        {m.supported ? (
          <>
            <Metric label="MFA enabled" value={m.mfa_on} />
            <Metric label="MFA disabled" value={m.mfa_off} />
            <Metric label="Admins without MFA" value={m.admin_mfa_off} />
          </>
        ) : (
          <Metric label="MFA" value="N/A" hint="No MFA column detected" />
        )}
      </div>

      {/* Admins without MFA */}
      {m.supported && stats?.admins_without_mfa?.length > 0 && (
        <div className="mt-3">
          <div className="section-title">Admins without MFA (top 25)</div>
          <div className="users-table-wrap" style={{ maxHeight: 220 }}>
            <table className="table table-dark align-middle mb-0">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {stats.admins_without_mfa.map(a => (
                  <tr key={a.username}>
                    <td>{a.username}</td>
                    <td>{a.email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Users by location */}
      {loc?.length > 0 && (
        <div className="mt-3">
          <div className="section-title">Users by location (top 20)</div>
          <div className="users-table-wrap" style={{ maxHeight: 260 }}>
            <table className="table table-dark align-middle mb-0">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {loc.map(r => (
                  <tr key={r.location}>
                    <td>{r.location}</td>
                    <td>{r.cnt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stale users list (≥ 90 days, excludes never logged) */}
      {stale.length > 0 && (
        <div className="mt-3">
          <div className="section-title">Stale users ≥ 90 days (top 50)</div>
          <div className="users-table-wrap" style={{ maxHeight: 260 }}>
            <table className="table table-dark align-middle mb-0">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Last login</th>
                </tr>
              </thead>
              <tbody>
                {stale.map(u => (
                  <tr key={u.username}>
                    <td>{u.username}</td>
                    <td>{u.email || "—"}</td>
                    <td>
                      {u.last_logged_in
                        ? new Date(u.last_logged_in).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {err && <div className="alert alert-danger mt-3 text-center py-2">{err}</div>}
    </div>
  );
}
/* ------------------------------------------------ */

export default React.memo(function Settings() {
  const [tab, setTab] = useState("users");

  const cacheKey = "admin:settings:v1";
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (settingsCache.has(cacheKey)) {
      setData(settingsCache.get(cacheKey));
      return;
    }
    if (inflight.has(cacheKey)) {
      inflight.get(cacheKey).then(setData).catch(() => {});
      return;
    }

    const controller = new AbortController();
    const p = axios
      .get("/api/admin/settings.php", { withCredentials: true, signal: controller.signal })
      .then(res => {
        const payload = res.data || { application: {}, security: {} };
        settingsCache.set(cacheKey, payload);
        return payload;
      })
      .then(setData)
      .catch(e => {
        if (e.name !== "CanceledError" && e.name !== "AbortError")
          setError("Failed to load settings");
      })
      .finally(() => inflight.delete(cacheKey));

    inflight.set(cacheKey, p);
    return () => controller.abort();
  }, []);

  const state = useMemo(() => {
    if (!data) return null;
    return {
      application: {
        timezone: data.application?.timezone ?? "UTC",
        maintenance: !!data.application?.maintenance,
        productName: data.application?.productName ?? "Cyber Portal",
      },
      security: {
        sessionMaxAge: Number(data.security?.sessionMaxAge ?? 3600),
        mfaRequired: !!data.security?.mfaRequired,
        passwordMinLen: Number(data.security?.passwordMinLen ?? 12),
      },
    };
  }, [data]);

  const setApplication = fn =>
    setData(prev => ({ ...(prev || {}), application: fn(prev?.application || {}) }));
  const setSecurity = fn =>
    setData(prev => ({ ...(prev || {}), security: fn(prev?.security || {}) }));

  const save = async () => {
    if (!state) return;
    setSaving(true);
    setError("");
    try {
      const body = { application: state.application, security: state.security };
      const res = await axios.post("/api/admin/settings.php", body, { withCredentials: true });
      const saved = res.data || body;
      settingsCache.set(cacheKey, saved);
      setData(saved);
    } catch {
      setError("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-wrapper">
      <div className="settings-header">
        <h1>Settings</h1>
        <div className="tabs">
          <button
            className={`tab-btn ${tab === "users" ? "active" : ""}`}
            onClick={() => setTab("users")}
          >
            Users
          </button>
          <button
            className={`tab-btn ${tab === "application" ? "active" : ""}`}
            onClick={() => setTab("application")}
          >
            Application
          </button>
          <button
            className={`tab-btn ${tab === "security" ? "active" : ""}`}
            onClick={() => setTab("security")}
          >
            Security
          </button>
        </div>
      </div>

      {tab === "users" && <AdminUsersTab />}

      {tab === "application" && state && (
        <div className="settings-card">
          <div className="settings-grid">
            <div className="info-row">
              <span>Product name</span>
              <input
                className="form-control form-control-sm bg-dark text-light border-0"
                value={state.application.productName}
                onChange={e => setApplication(p => ({ ...p, productName: e.target.value }))}
              />
            </div>
            <div className="info-row">
              <span>Default timezone</span>
              <input
                className="form-control form-control-sm bg-dark text-light border-0"
                value={state.application.timezone}
                onChange={e => setApplication(p => ({ ...p, timezone: e.target.value }))}
              />
            </div>
            <div className="info-row">
              <span>Maintenance mode</span>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={state.application.maintenance}
                  onChange={e => setApplication(p => ({ ...p, maintenance: e.target.checked }))}
                />
              </div>
            </div>
          </div>

          <div className="settings-actions">
            <button className="btn-profile btn-success" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              className="btn-profile btn-danger"
              onClick={() => window.location.reload()}
              disabled={saving}
            >
              Reload
            </button>
          </div>
          {error && <div className="alert alert-danger mt-3 text-center py-2">{error}</div>}
        </div>
      )}

      {tab === "security" && <SecurityOverview />}
    </div>
  );
});
